import { useEffect, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { CallAPI } from "../../apiManager/ApiCall";
import { publicURL } from "../../apiManager/APIConfig";
import { GetPublicCall } from "../../redux/api/public.api";

const MIN_IOS_KEY = "min_ios_version";
const MIN_ANDROID_KEY = "min_android_version";
const APP_STORE_KEY = "appstore_link";
const PLAY_STORE_KEY = "googleplay_link";

function rowData(rows, key) {
  const row = (Array.isArray(rows) ? rows : []).find((r) => r?.key === key);
  return row?.data == null ? "" : String(row.data);
}

function upsertOk(res) {
  return res?.status === 200 || res?.status === 201 || res?.skipped;
}

async function savePublicKey(key, data) {
  const value = String(data ?? "").trim();
  try {
    const editRes = await CallAPI({
      url: publicURL,
      method: "PUT",
      body: { key, data: value },
    });
    if (upsertOk(editRes)) return editRes;
    const missing =
      editRes?.status === 404 ||
      /not found/i.test(String(editRes?.message || ""));
    if (missing) {
      if (!value) return { status: 200, skipped: true };
      return (
        (await CallAPI({
          url: publicURL,
          method: "POST",
          body: { key, data: value },
        })) || {}
      );
    }
    return editRes || {};
  } catch {
    return { status: 400 };
  }
}

export default function ForceUpdateSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [minIos, setMinIos] = useState("");
  const [minAndroid, setMinAndroid] = useState("");
  const [appStoreLink, setAppStoreLink] = useState("");
  const [playStoreLink, setPlayStoreLink] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      const publicRes = await GetPublicCall();
      if (!mounted) return;
      const rows = Array.isArray(publicRes?.data) ? publicRes.data : [];
      setMinIos(rowData(rows, MIN_IOS_KEY));
      setMinAndroid(rowData(rows, MIN_ANDROID_KEY));
      setAppStoreLink(rowData(rows, APP_STORE_KEY));
      setPlayStoreLink(rowData(rows, PLAY_STORE_KEY));
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const onSave = async () => {
    setSaving(true);
    setMessage("");
    const results = await Promise.all([
      savePublicKey(MIN_IOS_KEY, minIos),
      savePublicKey(MIN_ANDROID_KEY, minAndroid),
      savePublicKey(APP_STORE_KEY, appStoreLink),
      savePublicKey(PLAY_STORE_KEY, playStoreLink),
    ]);
    setSaving(false);
    if (results.every(upsertOk)) {
      setMinIos(minIos.trim());
      setMinAndroid(minAndroid.trim());
      setAppStoreLink(appStoreLink.trim());
      setPlayStoreLink(playStoreLink.trim());
      setMessage(
        "Saved. The app shows a blocking update screen when the installed version is below these minima.",
      );
      return;
    }
    setMessage("Could not save one or more public keys. Try again.");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox px={3} pt={3} pb={3}>
        <Typography variant="h5" fontWeight={700}>
          Force update
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.75, mb: 3, maxWidth: 720 }}
        >
          These values are stored in the public table and read by the mobile
          app. If a user’s installed version is lower than the minimum for
          their platform, they cannot dismiss the update screen until they
          open the store.
        </Typography>

        <Box
          sx={{
            maxWidth: 720,
            p: 2.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <TextField
            fullWidth
            label="Minimum iOS version"
            value={minIos}
            onChange={(e) => setMinIos(e.target.value)}
            helperText="Public key min_ios_version. Example: 10.2.0. Leave blank to skip forcing iOS."
            disabled={loading}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Minimum Android version"
            value={minAndroid}
            onChange={(e) => setMinAndroid(e.target.value)}
            helperText="Public key min_android_version. Example: 10.2.0. Leave blank to skip forcing Android."
            disabled={loading}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="App Store link"
            value={appStoreLink}
            onChange={(e) => setAppStoreLink(e.target.value)}
            helperText="Public key appstore_link. Used by the Update button on iOS."
            disabled={loading}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Google Play link"
            value={playStoreLink}
            onChange={(e) => setPlayStoreLink(e.target.value)}
            helperText="Public key googleplay_link. Used by the Update button on Android."
            disabled={loading}
            sx={{ mb: 2.5 }}
          />

          <MDButton
            variant="gradient"
            color="info"
            onClick={onSave}
            disabled={loading || saving}
          >
            {saving ? "Saving…" : "Save"}
          </MDButton>

          {message ? (
            <Typography variant="body2" sx={{ mt: 2 }}>
              {message}
            </Typography>
          ) : null}
        </Box>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
